import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { toast } from "sonner";

const BARS = [10, 18, 26, 14, 30, 22, 12, 28, 16, 24, 12, 20, 26, 14];

const fmt = (s) => {
  if (!isFinite(s)) return "--:--";
  const v = s || 0;
  return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(Math.floor(v % 60)).padStart(2, "0")}`;
};

export default function AudioPlayer({
  src,
  testId = "audio-player",
  emptyMessage = "L'audio sera bientôt disponible.",
  onPlay,
  onPause,
  onEnd,
  onDuration,
}) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!src) return;
    const a = new Audio(src);
    audioRef.current = a;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => {
      setDuration(a.duration);
      onDuration?.(a.duration);
    };
    const onEnd = () => {
      setPlaying(false);
      setTime(0);
      onEnd?.();
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
      audioRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    const stop = () => {
      if (audioRef.current && playing) {
        audioRef.current.pause();
        setPlaying(false);
        onPause?.();
      }
    };
    window.addEventListener("memorium-stop-audio", stop);
    return () => window.removeEventListener("memorium-stop-audio", stop);
  }, [playing, onPause]);

  const toggle = async () => {
    if (!src) {
      toast.info(emptyMessage);
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      onPause?.();
      return;
    }
    try {
      await a.play();
      setPlaying(true);
      onPlay?.();
    } catch {
      toast.error("Lecture impossible pour le moment.");
    }
  };

  const progress = duration ? time / duration : 0;

  return (
    <div
      data-testid={testId}
      className="flex items-center gap-4 rounded-2xl border border-[#C5A059]/30 bg-[#F4EFEA] p-4"
    >
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={toggle}
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#C5A059] text-[#1C1917] transition-colors duration-300 hover:bg-[#D4AF37]"
        aria-label="Lecture / Pause"
      >
        {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
      </button>
      <div className="flex h-8 flex-1 items-center justify-between gap-1">
        {BARS.map((h, i) => (
          <span
            key={i}
            className={`w-1 rounded-full transition-colors duration-300 ${
              i / BARS.length <= progress ? "bg-[#C5A059]" : "bg-[#C5A059]/40"
            } ${playing ? "wave-bar" : ""}`}
            style={{ height: h, animationDelay: `${i * 0.09}s` }}
          />
        ))}
      </div>
      <span className="text-xs font-medium tabular-nums text-stone-500">
        {fmt(time)} / {src ? fmt(duration) : "--:--"}
      </span>
    </div>
  );
}
